import { EditableLoadstarValues } from "../../../src/models/loadstar-model";
import { WeatherLimits } from "../../../src/models/voyage-routing-model";

export type ReviewRow = {
    label: string;
    current: string;
    updated: string | number;
    field: keyof EditableLoadstarValues;
    inputMode: 'decimal' | 'integer';
    unit: string;
    step?: string;
};

export type EditableLoadstarFormValues = Record<keyof EditableLoadstarValues, string>;
export type EditableFieldPrecision = Record<keyof EditableLoadstarValues, number>;
export type EditableLoadstarValidationErrors = Partial<Record<keyof EditableLoadstarValues, string>>;

export const EDITABLE_FIELD_PRECISION: EditableFieldPrecision = {
    fore_draft: 2,
    aft_draft: 2,
    fluid_gm: 2,
    solid_gm: 2,
    live_reefers: 0,
    max_roll_angle: 2,
};

export function getLoadstarMaxRollAngle(): number {
    try {
        const weatherLimits = localStorage.getItem("voyage-routing-model-weather-limits");
        if (weatherLimits) {
            const { MaxRollAngle } = JSON.parse(weatherLimits) as WeatherLimits;
            return MaxRollAngle?.warn ?? MaxRollAngle?.avoid ?? 13;
        }

        console.info("No weather limits found in local storage");
        return 13;
    } catch (error) {
        console.info("Error fetching max roll angle value", error);
        return 13;
    }
}

export function parseLoadstarDecimalValue(value: string): number | null {
    const normalized = value.trim().replace(',', '.');
    if (!normalized) {
        return null;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
}

export function parseLoadstarIntegerValue(value: string): number | null {
    const trimmed = value.trim();
    if (!trimmed || !/^\d+$/.test(trimmed)) {
        return null;
    }

    return Number(trimmed);
}

export function validateLoadstarEditingValues(
    values: EditableLoadstarFormValues,
    maxRollAngle = 20,
): EditableLoadstarValidationErrors {
    const errors: EditableLoadstarValidationErrors = {};
    const foreDraft = parseLoadstarDecimalValue(values.fore_draft);
    const aftDraft = parseLoadstarDecimalValue(values.aft_draft);
    const fluidGm = parseLoadstarDecimalValue(values.fluid_gm);
    const solidGm = parseLoadstarDecimalValue(values.solid_gm);
    const liveReefers = parseLoadstarIntegerValue(values.live_reefers);
    const rollAngle = parseLoadstarDecimalValue(values.max_roll_angle);

    const foreDraftError = getDraftValidationError(foreDraft, 'Fore Draft');
    if (foreDraftError) {
        errors.fore_draft = foreDraftError;
    }

    const aftDraftError = getDraftValidationError(aftDraft, 'Aft Draft');
    if (aftDraftError) {
        errors.aft_draft = aftDraftError;
    }

    const fluidGmError = getFluidGmValidationError(fluidGm, values.fluid_gm);
    if (fluidGmError) {
        errors.fluid_gm = fluidGmError;
    }

    const solidGmError = getSolidGmValidationError(solidGm, values.solid_gm, fluidGm);
    if (solidGmError) {
        errors.solid_gm = solidGmError;
    }

    const liveReefersError = getLiveReefersValidationError(liveReefers, values.live_reefers);
    if (liveReefersError) {
        errors.live_reefers = liveReefersError;
    }

    const rollAngleError = getRollAngleValidationError(rollAngle, maxRollAngle);
    if (rollAngleError) {
        errors.max_roll_angle = rollAngleError;
    }

    return errors;
}

function getDraftValidationError(value: number | null, label: 'Fore Draft' | 'Aft Draft'): string {
    if (value === null) {
        return `${label} is required.`;
    }

    if (!(value > 0 && value <= 20)) {
        return `${label} must be greater than 0 m and at most 20 m.`;
    }

    return '';
}

function getFluidGmValidationError(value: number | null, rawValue: string): string {
    if (!rawValue.trim()) {
        return 'Fluid GM is required.';
    }

    if (value === null) {
        return 'Please enter a valid number (meters).';
    }

    if (value < 0.6 || value > 25.0) {
        return 'Fluid GM must be between 0.6 m and 25.0 m.';
    }

    return '';
}

function getSolidGmValidationError(value: number | null, rawValue: string, fluidGm: number | null): string {
    if (!rawValue.trim()) {
        return 'Solid GM is required.';
    }

    if (value === null) {
        return 'Please enter a valid number (meters).';
    }

    if (value < 0.6 || value > 25.0) {
        return 'Solid GM must be between 0.6 m and 25.0 m.';
    }

    if (fluidGm !== null && value < fluidGm) {
        return 'Solid GM must be greater than or equal to Fluid GM.';
    }

    if (fluidGm !== null && value > fluidGm + 5.0) {
        return 'Solid GM must not be more than 5.0 m greater than Fluid GM.';
    }

    return '';
}

function getLiveReefersValidationError(value: number | null, rawValue: string): string {
    if (!rawValue.trim()) {
        return 'Live Reefers is required.';
    }

    if (value === null || !Number.isInteger(value)) {
        return 'Live Reefers must be a whole number.';
    }

    if (value < 0 || value > 3000) {
        return 'Live Reefers must be between 0 and 3000.';
    }

    return '';
}

function getRollAngleValidationError(value: number | null, maxRollAngle: number): string {
    if (value === null) {
        return 'Maximum Roll Angle is required.';
    }

    if (value < 5 || value > maxRollAngle) {
        return `Maximum Roll Angle must be between 5° and ${maxRollAngle}°.`;
    }

    return '';
}
